<template>
  <a-select
    show-search
    :mode="multiply ? 'multiple' : 'default'"
    v-model="templateProxy"
    :autoClearSearchValue="false"
    :allow-clear="true"
    :loading="loadingSequences"
    :filter-option="false"
    :disabled="disabled"
    :placeholder="loadingSequences ? 'Loading...' : 'Start typing to search In Service'"
    @search="searchTemplates"
    @focus="focusTemplates"
    @blur="blurTemplates"
  >
    <a-select-option v-for="sequence in sequences" :key="sequence.id" :value="sequence.id">
      {{ sequence.name }} </a-select-option
    ><template slot="dropdownRender" slot-scope="menu">
      <a-spin tip="Loading..." :spinning="loadingSequences">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
  </a-select>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import VNodes from '@/components/ui/v-nodes.vue'
import { notification, Select, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: sequenceActions } = createNamespacedHelpers('sequence')

export default {
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-spin': Spin,
    VNodes
  },
  props: {
    value: {
      type: Number || Array,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiply: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      loadingSequences: false,
      sequence: [],
      sequences: [],

      searchQuery: ''
    }
  },
  watch: {
    searchQuery: {
      handler() {
        this.fetchTemplates()
      },
      immediate: true
    },
    value: {
      handler(newVal) {
        const alreadyExist = this.multiply
          ? (newVal ?? []).every(sequence => this.sequences.find(item => item.id === sequence.id))
          : this.sequences.find(item => item.id === newVal)

        const needFetch = this.multiply ? newVal?.length : !!newVal

        if (newVal && !alreadyExist && needFetch) {
          this.fetchTemplate()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    templateProxy: {
      get() {
        if (Array.isArray(this.value)) {
          return this.value.map(item => item.id)
        }
        return this.value ? (this.value?.id ?? this.value) : undefined
      },
      set(value) {
        if (value === undefined) {
          this.searchQuery = ''

          this.$emit('update', undefined)

          return
        }

        this.sequence = this.multiply
          ? this.sequences.filter(item => value.includes(item.id))
          : this.sequences.find(item => item.id === value)

        this.$emit('update', this.sequence)
      }
    }
  },
  methods: {
    ...sequenceActions(['getAllSequence']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    focusTemplates() {
      this.searchQuery = ''
    },

    blurTemplates() {
      this.searchQuery = ''
    },

    searchTemplates: debounce(function (value) {
      this.searchQuery = value
    }, 1000),

    async fetchTemplates() {
      this.loadingSequences = true
      try {
        const { type, ...rest } = this.filters
        const data = await this.getAllSequence({
          filter: {
            ...rest,
            name: this.searchQuery
          },
          type,
          page: 1,
          size: 10,
          cancelPreviousRequests: false
        })

        if (this.sequence.length) {
          const templateIds = new Set(this.sequence.map(item => item.id))

          this.sequences = [...this.sequence, ...data.items.filter(item => !templateIds.has(item.id))]
        } else {
          this.sequences = [...data.items]
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loadingSequences = false
      }
    },

    async fetchTemplate() {
      try {
        const { type, ...rest } = this.filters

        const data = await this.getAllSequence({
          filter: {
            ...rest,
            ids: this.multiply || Array.isArray(this.value) ? this.value : [this.value]
          },
          type,
          size: 1000,
          cancelPreviousRequests: false
        })

        this.sequence = data.items

        if (this.sequence.length) {
          const templateIds = new Set(this.sequence.map(item => item.id))

          this.sequences = [...this.sequence, ...this.sequences.filter(item => !templateIds.has(item.id))]
        }

        this.$emit('update', this.multiply ? this.sequence : this.sequence?.[0])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        this.sequence = []
        this.$emit('update', this.multiply ? this.sequence : undefined)
      }
    }
  }
}
</script>

<style scoped></style>
